import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  ButtonGroup,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Alert,
  Chip,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Autocomplete,
  TextField,
  CircularProgress,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Delete as DeleteIcon,
  CheckCircle as CheckIcon,
  Undo as ReturnIcon,
  SwapHoriz as MoveIcon,
  Build as InstallIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import ScanInput from './ScanInput';
import useScanQueue from '../hooks/useScanQueue';
import api, { jobApi } from '../services/api.service';
import { useAuth } from '../contexts/AuthContext';
import { getStatusDisplay } from '../constants/equipmentStatus';

/**
 * QuickActionsCore - Shared logic for Quick Actions functionality
 * Used by both QuickActionsPage and QuickActionsModal
 */
export default function QuickActionsCore({
  preselectedJob = null,
  compact = false,
  autoScanId = null
}) {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const {
    queue,
    loading: queueLoading,
    error: queueError,
    addToQueue,
    removeFromQueue,
    clearQueue,
    clearError
  } = useScanQueue();

  const [selectedAction, setSelectedAction] = useState(null);
  const [processing, setProcessing] = useState(false);

  // Action-specific state
  const [techName, setTechName] = useState('');
  const [targetJobId, setTargetJobId] = useState('');
  const [targetSiteId, setTargetSiteId] = useState('');

  // Technician list
  const [technicians, setTechnicians] = useState([]);
  const [loadingTechs, setLoadingTechs] = useState(false);

  // Job list and search
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [jobSearchText, setJobSearchText] = useState('');
  const [showClosedJobs, setShowClosedJobs] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  // Sites for selected job
  const [sites, setSites] = useState([]);
  const [loadingSites, setLoadingSites] = useState(false);

  // Pre-populate job if provided
  useEffect(() => {
    if (preselectedJob) {
      setTargetJobId(preselectedJob.id || '');
    }
  }, [preselectedJob]);

  // Auto-scan equipment if ID provided (from URL or prop)
  useEffect(() => {
    if (autoScanId) {
      handleScan(autoScanId);
    }
  }, [autoScanId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load technicians on mount
  useEffect(() => {
    const loadTechnicians = async () => {
      setLoadingTechs(true);
      try {
        const response = await api.get('/auth/technicians');
        const technicians = Array.isArray(response.data) ? response.data : (response.data?.data || []);
        setTechnicians(technicians);
      } catch (err) {
        console.error('Error loading technicians:', err);
      } finally {
        setLoadingTechs(false);
      }
    };
    loadTechnicians();

    const loadJobs = async () => {
      setLoadingJobs(true);
      try {
        const response = await jobApi.getAllJobs();
        const jobsData = response.data?.jobs || [];
        setJobs(Array.isArray(jobsData) ? jobsData : []);
      } catch (err) {
        console.error('Error loading jobs:', err);
        enqueueSnackbar('Failed to load jobs', { variant: 'error' });
      } finally {
        setLoadingJobs(false);
      }
    };
    loadJobs();
  }, [enqueueSnackbar]);

  // Filter jobs based on search text and closed jobs toggle
  const filteredJobs = useCallback(() => {
    if (!Array.isArray(jobs)) return [];

    let filtered = jobs;

    // Filter out closed/completed jobs unless toggle is on
    if (!showClosedJobs) {
      filtered = filtered.filter(job =>
        job.status !== 'Completed' && job.status !== 'Closed'
      );
    }

    // Apply search filter
    if (jobSearchText && jobSearchText.length >= 1) {
      const searchLower = jobSearchText.toLowerCase();
      filtered = filtered.filter(job => {
        const jobNumber = (job.job_number || job.id || '').toString().toLowerCase();
        const jobName = (job.name || '').toLowerCase();
        const client = (job.client || '').toLowerCase();

        return jobNumber.includes(searchLower) ||
               jobName.includes(searchLower) ||
               client.includes(searchLower);
      });
    }

    return filtered;
  }, [jobs, showClosedJobs, jobSearchText]);

  // Load sites when job is selected
  useEffect(() => {
    const loadSites = async () => {
      if (!targetJobId) {
        setSites([]);
        setTargetSiteId('');
        return;
      }

      setLoadingSites(true);
      try {
        const response = await api.get(`/sites/job/${targetJobId}`);
        const sitesData = response.data?.data || response.data || [];
        setSites(Array.isArray(sitesData) ? sitesData : []);
        // Auto-select first site if only one
        if (sitesData.length === 1) {
          setTargetSiteId(sitesData[0].id);
        }
      } catch (err) {
        console.error('Error loading sites:', err);
        setSites([]);
      } finally {
        setLoadingSites(false);
      }
    };
    loadSites();
  }, [targetJobId]);

  // Check user permissions
  const userRoles = user?.roles || [];
  const isWarehouse = userRoles.includes('warehouse') || userRoles.includes('admin') || userRoles.includes('manager');
  const isTech = userRoles.includes('tech');
  const isAdmin = userRoles.includes('admin') || userRoles.includes('manager');

  // Get all unique statuses from scanned items
  const scannedStatuses = [...new Set(queue.map(item => item.status))];

  // Smart action filtering based on role AND current item statuses
  const availableActions = (() => {
    // Base actions by role
    let roleActions = [];
    if (isWarehouse || isAdmin) {
      roleActions = ['assign-to-job', 'checkout', 'return-to-job', 'return-to-warehouse', 'move-job', 'install'];
    } else if (isTech) {
      roleActions = ['install'];
    }

    // Filter actions based on scanned item statuses
    const validActions = roleActions.filter(action => {
      switch (action) {
        case 'assign-to-job':
          return scannedStatuses.every(s => s === 'available' || s === 'returned');

        case 'checkout':
          return scannedStatuses.every(s => s === 'available' || s === 'assigned' || s === 'returned');

        case 'return-to-job':
          return scannedStatuses.every(s => ['checked_out', 'installed'].includes(s));

        case 'return-to-warehouse':
          return scannedStatuses.every(s => ['assigned', 'returned', 'checked_out', 'installed'].includes(s));

        case 'move-job':
          return scannedStatuses.every(s => s === 'assigned' || s === 'returned');

        case 'install':
          return scannedStatuses.every(s => s === 'checked_out');

        default:
          return true;
      }
    });

    return validActions;
  })();

  // Handle scan
  const handleScan = async (barcode) => {
    const success = await addToQueue(barcode);
    if (!success) {
      enqueueSnackbar(queueError || 'Failed to add item', { variant: 'error' });
    }
    return success;
  };

  // Reset action selection
  const handleActionChange = (action) => {
    setSelectedAction(action);
    setTechName('');
    // Don't reset targetJobId if preselected
  };

  // Process selected action
  const handleExecuteAction = async () => {
    if (queue.length === 0) {
      enqueueSnackbar('No items scanned', { variant: 'warning' });
      return;
    }

    if (!selectedAction) {
      enqueueSnackbar('Please select an action', { variant: 'warning' });
      return;
    }

    setProcessing(true);

    try {
      let response;
      const items = queue.map(item => ({ equipment_id: item.equipment_id }));

      switch (selectedAction) {
        case 'assign-to-job':
          if (!targetJobId.trim()) {
            enqueueSnackbar('Please select a job', { variant: 'warning' });
            setProcessing(false);
            return;
          }
          const assignResults = { success: [], errors: [] };
          for (const item of queue) {
            try {
              // Backend now automatically updates job_equipment_plans.assigned_quantity
              // when status changes to 'assigned', so we can use the simple PUT endpoint
              const updateData = {
                current_job_id: targetJobId,
                status: 'assigned',
                last_changed_by: user?.id || 'system'
              };

              if (targetSiteId) {
                updateData.current_site_id = targetSiteId;
              } else {
                updateData.current_site_id = null;
              }

              await api.put(`/equipment-items/${item.id}`, updateData);
              assignResults.success.push(item.equipment_id);
            } catch (err) {
              assignResults.errors.push({
                equipment_id: item.equipment_id,
                reason: err.response?.data?.message || 'Failed to assign'
              });
            }
          }
          response = { data: { success: true, data: assignResults } };
          break;

        case 'checkout':
          if (!techName.trim()) {
            enqueueSnackbar('Please enter technician name', { variant: 'warning' });
            setProcessing(false);
            return;
          }
          response = await api.post('/equipment-workflow/batch-checkout', {
            items,
            assignedTo: techName,
            userId: user?.id || 'system'
          });
          break;

        case 'return-to-job':
          response = await api.post('/equipment-workflow/batch-return', {
            items: queue.map(item => ({
              equipment_id: item.equipment_id,
              returnType: 'to_job'
            })),
            userId: user?.id || 'system'
          });
          break;

        case 'return-to-warehouse':
          response = await api.post('/equipment-workflow/batch-return', {
            items: queue.map(item => ({
              equipment_id: item.equipment_id,
              returnType: 'to_warehouse'
            })),
            userId: user?.id || 'system'
          });
          break;

        case 'move-job':
          if (!targetJobId.trim()) {
            enqueueSnackbar('Please select a target job', { variant: 'warning' });
            setProcessing(false);
            return;
          }
          // First return to warehouse
          await api.post('/equipment-workflow/batch-return', {
            items: queue.map(item => ({
              equipment_id: item.equipment_id,
              returnType: 'to_warehouse'
            })),
            userId: user?.id || 'system'
          });

          // Then assign to new job
          // Backend now automatically updates job_equipment_plans.assigned_quantity
          const moveResults = { success: [], errors: [] };
          for (const item of queue) {
            try {
              const updateData = {
                current_job_id: targetJobId,
                status: 'assigned',
                last_changed_by: user?.id || 'system'
              };

              if (targetSiteId) {
                updateData.current_site_id = targetSiteId;
              } else {
                updateData.current_site_id = null;
              }

              await api.put(`/equipment-items/${item.id}`, updateData);
              moveResults.success.push(item.equipment_id);
            } catch (err) {
              moveResults.errors.push({
                equipment_id: item.equipment_id,
                reason: err.response?.data?.message || 'Failed to reassign'
              });
            }
          }
          response = { data: { success: true, data: moveResults } };
          break;

        case 'install':
          const installResults = { success: [], errors: [] };
          for (const item of queue) {
            try {
              await api.post(`/equipment-workflow/install/${item.equipment_id}`, {
                userId: user?.id || 'system',
                userName: user?.name
              });
              installResults.success.push(item.equipment_id);
            } catch (err) {
              installResults.errors.push({
                equipment_id: item.equipment_id,
                reason: err.response?.data?.message || 'Failed'
              });
            }
          }
          response = { data: { success: true, data: installResults } };
          break;

        default:
          enqueueSnackbar('Unknown action', { variant: 'error' });
          setProcessing(false);
          return;
      }

      if (response?.data) {
        const results = response.data.data || response.data;
        const successItems = results.success || [];
        const errorItems = results.errors || [];

        if (successItems.length > 0) {
          enqueueSnackbar(
            `Successfully processed ${successItems.length} items`,
            { variant: 'success' }
          );
        }

        if (errorItems.length > 0) {
          enqueueSnackbar(
            `${errorItems.length} items failed`,
            { variant: 'error' }
          );
          console.error('Action errors:', errorItems);
        }

        // Clear queue on success
        if (successItems.length > 0) {
          clearQueue();
          setSelectedAction(null);
          setTechName('');
          // Keep targetJobId if preselected
          if (!preselectedJob) {
            setTargetJobId('');
          }

          // Dispatch custom event to notify other components to refresh
          // This allows Job Planning pages and other views to update automatically
          const eventDetail = {
            action: selectedAction,
            itemCount: successItems.length,
            timestamp: new Date().toISOString()
          };
          console.log('📢 QuickActions dispatching equipmentStatusChanged event:', eventDetail);
          window.dispatchEvent(new CustomEvent('equipmentStatusChanged', { detail: eventDetail }));
        }
      }

    } catch (err) {
      console.error('Action error:', err);
      enqueueSnackbar(
        err.response?.data?.message || 'Failed to process action',
        { variant: 'error' }
      );
    } finally {
      setProcessing(false);
    }
  };

  const hasItems = queue.length > 0;
  const buttonSize = compact ? 'small' : 'medium';

  return (
    <Box>
      {/* Scan Input */}
      <Box sx={{ mb: 3 }}>
        <Typography variant={compact ? "subtitle1" : "h6"} gutterBottom>
          {compact ? "Scan Equipment" : "Scan or Search Equipment"}
        </Typography>
        <ScanInput
          onScan={handleScan}
          placeholder="Scan barcode, QR code, or type equipment ID..."
          disabled={processing}
        />
        {queueError && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={clearError}>
            {queueError}
          </Alert>
        )}
      </Box>

      {/* Scanned Items Queue */}
      {hasItems && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant={compact ? "subtitle1" : "h6"}>
              Scanned Items ({queue.length})
            </Typography>
            <Button
              size="small"
              onClick={clearQueue}
              disabled={processing}
            >
              Clear All
            </Button>
          </Box>

          <List
            dense={compact}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              maxHeight: compact ? 200 : 300,
              overflow: 'auto'
            }}
          >
            {queue.slice().reverse().map((item, index) => (
              <Box key={item.id}>
                <ListItem
                  secondaryAction={
                    <IconButton
                      edge="end"
                      onClick={() => removeFromQueue(item.id)}
                      disabled={processing}
                      size="small"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={`${item.equipment_id} - ${item.description || 'No description'}`}
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Chip
                          label={getStatusDisplay(item.status).label}
                          size="small"
                          sx={{
                            backgroundColor: getStatusDisplay(item.status).color,
                            color: 'white',
                            fontWeight: 500,
                            height: 20,
                            fontSize: '0.7rem'
                          }}
                        />
                        {(item.job_name || item.job_number || item.current_job_id) && (
                          <Typography variant="caption" color="text.secondary">
                            Job: {item.job_name || item.job_number || item.current_job_id}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
                {index < queue.length - 1 && <Divider />}
              </Box>
            ))}
          </List>
        </Box>
      )}

      {/* Action Selection */}
      {hasItems && (
        <Box>
          <Typography variant={compact ? "subtitle1" : "h6"} gutterBottom>
            Choose Action
          </Typography>

          {/* First row of actions */}
          {availableActions.some(a => ['assign-to-job', 'checkout', 'return-to-job', 'return-to-warehouse'].includes(a)) && (
            <ButtonGroup variant="outlined" fullWidth sx={{ mb: 2 }}>
              {availableActions.includes('assign-to-job') && (
                <Button
                  startIcon={<MoveIcon />}
                  variant={selectedAction === 'assign-to-job' ? 'contained' : 'outlined'}
                  onClick={() => handleActionChange('assign-to-job')}
                  disabled={processing}
                  size={buttonSize}
                >
                  Assign to Job
                </Button>
              )}
              {availableActions.includes('checkout') && (
                <Button
                  startIcon={<CheckIcon />}
                  variant={selectedAction === 'checkout' ? 'contained' : 'outlined'}
                  onClick={() => handleActionChange('checkout')}
                  disabled={processing}
                  size={buttonSize}
                >
                  Check Out
                </Button>
              )}
              {availableActions.includes('return-to-job') && (
                <Button
                  startIcon={<ReturnIcon />}
                  variant={selectedAction === 'return-to-job' ? 'contained' : 'outlined'}
                  onClick={() => handleActionChange('return-to-job')}
                  disabled={processing}
                  size={buttonSize}
                >
                  Return to Job
                </Button>
              )}
              {availableActions.includes('return-to-warehouse') && (
                <Button
                  startIcon={<ReturnIcon />}
                  variant={selectedAction === 'return-to-warehouse' ? 'contained' : 'outlined'}
                  onClick={() => handleActionChange('return-to-warehouse')}
                  disabled={processing}
                  size={buttonSize}
                >
                  Return to Warehouse
                </Button>
              )}
            </ButtonGroup>
          )}

          {/* Second row of actions */}
          {availableActions.some(a => ['move-job', 'install'].includes(a)) && (
            <ButtonGroup variant="outlined" fullWidth sx={{ mb: 2 }}>
              {availableActions.includes('move-job') && (
                <Button
                  startIcon={<MoveIcon />}
                  variant={selectedAction === 'move-job' ? 'contained' : 'outlined'}
                  onClick={() => handleActionChange('move-job')}
                  disabled={processing}
                  size={buttonSize}
                >
                  Transfer
                </Button>
              )}
              {availableActions.includes('install') && (
                <Button
                  startIcon={<InstallIcon />}
                  variant={selectedAction === 'install' ? 'contained' : 'outlined'}
                  onClick={() => handleActionChange('install')}
                  disabled={processing}
                  size={buttonSize}
                >
                  Mark as Installed
                </Button>
              )}
            </ButtonGroup>
          )}

          {/* Action-Specific Inputs */}
          {selectedAction === 'assign-to-job' && (
            <>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showClosedJobs}
                        onChange={(e) => setShowClosedJobs(e.target.checked)}
                        size="small"
                      />
                    }
                    label={<Typography variant="caption">Show closed jobs</Typography>}
                  />
                </Box>
                <Autocomplete
                  options={filteredJobs()}
                  value={selectedJob}
                  onChange={(event, newValue) => {
                    setSelectedJob(newValue);
                    setTargetJobId(newValue?.id || '');
                  }}
                  inputValue={jobSearchText}
                  onInputChange={(event, newInputValue) => setJobSearchText(newInputValue)}
                  loading={loadingJobs}
                  disabled={processing || loadingJobs}
                  getOptionLabel={(option) => `${option.job_number || option.id} • ${option.name || option.client}`}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box sx={{ width: '100%' }}>
                        <Typography variant="body2" component="div" sx={{ fontWeight: 500 }}>
                          Job {option.job_number || option.id} • {option.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                          Client: {option.client || 'Not specified'}
                          {option.status && (
                            <Chip
                              label={option.status}
                              size="small"
                              sx={{ ml: 1, height: 18, fontSize: '0.65rem' }}
                              color={option.status === 'Active' ? 'success' : 'default'}
                            />
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Job"
                      placeholder="Type to filter jobs..."
                      helperText="Shows active jobs - type to filter by job number, name, or client"
                      size={compact ? "small" : "medium"}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingJobs ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  filterOptions={(x) => x}
                  noOptionsText={jobSearchText.length > 0 ? "No jobs match your search" : "No active jobs available"}
                />
              </Box>

              {targetJobId && sites.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <FormControl fullWidth disabled={processing || loadingSites} size={compact ? "small" : "medium"}>
                    <InputLabel>Select Site (Optional)</InputLabel>
                    <Select
                      value={targetSiteId}
                      onChange={(e) => setTargetSiteId(e.target.value)}
                      label="Select Site (Optional)"
                    >
                      <MenuItem value="">
                        <em>Job-level (no specific site)</em>
                      </MenuItem>
                      {sites.map(site => (
                        <MenuItem key={site.id} value={site.id}>
                          {site.name}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {loadingSites ? 'Loading sites...' : 'Leave blank for job-level assignment'}
                    </FormHelperText>
                  </FormControl>
                </Box>
              )}
            </>
          )}

          {selectedAction === 'checkout' && (
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth disabled={processing || loadingTechs} size={compact ? "small" : "medium"}>
                <InputLabel>Select Technician</InputLabel>
                <Select
                  value={techName}
                  onChange={(e) => setTechName(e.target.value)}
                  label="Select Technician"
                >
                  {technicians.map((tech) => (
                    <MenuItem key={tech.id} value={tech.name}>
                      {tech.name} ({tech.email})
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {loadingTechs ? 'Loading technicians...' : 'Items will be checked out to this technician'}
                </FormHelperText>
              </FormControl>
            </Box>
          )}

          {selectedAction === 'move-job' && (
            <>
              <Box sx={{ mb: 2 }}>
                <FormControl fullWidth disabled={processing || loadingJobs} size={compact ? "small" : "medium"}>
                  <InputLabel>Transfer to Job</InputLabel>
                  <Select
                    value={targetJobId}
                    onChange={(e) => setTargetJobId(e.target.value)}
                    label="Transfer to Job"
                  >
                    <MenuItem value="">
                      <em>Select destination job...</em>
                    </MenuItem>
                    {jobs.map(job => (
                      <MenuItem key={job.id} value={job.id}>
                        {job.job_number || job.id} - {job.name || job.client}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Items will be transferred to this job
                  </FormHelperText>
                </FormControl>
              </Box>

              {targetJobId && sites.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <FormControl fullWidth disabled={processing || loadingSites} size={compact ? "small" : "medium"}>
                    <InputLabel>Transfer to Site (Optional)</InputLabel>
                    <Select
                      value={targetSiteId}
                      onChange={(e) => setTargetSiteId(e.target.value)}
                      label="Transfer to Site (Optional)"
                    >
                      <MenuItem value="">
                        <em>Job-level (no specific site)</em>
                      </MenuItem>
                      {sites.map(site => (
                        <MenuItem key={site.id} value={site.id}>
                          {site.name}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {loadingSites ? 'Loading sites...' : 'Leave blank for job-level transfer'}
                    </FormHelperText>
                  </FormControl>
                </Box>
              )}
            </>
          )}

          {selectedAction === 'return-to-job' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Items will be marked as 'returned' - staying on their current job but cleared from technician checkout
            </Alert>
          )}

          {selectedAction === 'return-to-warehouse' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Items will be marked as 'available' - removed from job and returned to warehouse inventory
            </Alert>
          )}

          {selectedAction === 'install' && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Items will be marked as 'installed' at their job site
            </Alert>
          )}

          {/* Execute Button */}
          {selectedAction && (
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handleExecuteAction}
              disabled={processing || queueLoading}
              sx={{ py: 1.5 }}
            >
              {processing ? 'Processing...' : `Execute Action on ${queue.length} Items`}
            </Button>
          )}
        </Box>
      )}

      {/* Help Text */}
      {!hasItems && (
        <Alert severity="info">
          Scan equipment items using a barcode scanner or camera, then select an action to perform on all scanned items at once.
        </Alert>
      )}
    </Box>
  );
}
